import React, { useCallback, useEffect, useState } from 'react';
import {
    PullToRefresh, Toast, Empty, Popup, SearchBar, Input
} from 'antd-mobile';
import {
    RightOutline, FilterOutline, EditSOutline
} from 'antd-mobile-icons';
import { useNavigate } from 'react-router-dom';
import './index.css';
import { useAuth } from '../app/providers/AuthProvider';
import { memberApi } from '../features/member/api/memberApi';
import Icon from '../icon';

const CUSTOM_TAG_VALUE = '__custom__';

// 职务名称只负责展示；是否参与带队由 standingMember 单独控制。
const ROLE_TAG_OPTIONS = [
    { label: '无职务', value: '', icon: '👤' },
    { label: '常委', value: '常委', icon: '⭐' },
    { label: '秘书', value: '秘书', icon: '📋' },
    { label: '领队', value: '领队', icon: '🧭' },
    { label: '荣誉常委', value: '荣誉常委', icon: '🏅' },
    { label: '自定义', value: CUSTOM_TAG_VALUE, icon: '✦' },
];

const MEMBER_STATUS_OPTIONS = [
    { label: '正式队员', value: null, icon: '⚽', description: '恢复正式身份' },
    { label: '临时队员', value: 1, icon: '⏳', description: '临时参与活动' },
    { label: '非正式队员', value: 3, icon: '🌱', description: '不计入正式名单' },
    { label: '离队队员', value: 4, icon: '👋', description: '保留历史数据' },
];

const getLegacyRoleTag = (item) => {
    const tagInfo = typeof item?.tagInfo === 'string' ? item.tagInfo.trim() : '';
    if (tagInfo) return tagInfo;
    if (Number(item?.standingMember) === 1) return '常委';
    if (Number(item?.standingMember) === 3) return '领队';
    return '';
};

const normalizeMemberStatus = (tempMember) => {
    const status = Number(tempMember);
    return [1, 3, 4].includes(status) ? status : null;
};

export default function Member() {

    const navigate = useNavigate()
    const { memberInfo } = useAuth()
    const [userList, setUserList] = useState([])
    const [orderInfo, setOrderInfo] = useState(1)
    const [activeTab, setActiveTab] = useState('members'); // 'members' or 'tempMembers'
    const [searchText, setSearchText] = useState('');
    const [editingMember, setEditingMember] = useState(null);
    const [roleSaving, setRoleSaving] = useState(false);
    const [roleDraft, setRoleDraft] = useState({
        selectedTag: '',
        customTag: '',
        leadsTeam: false,
        tempMember: null,
    });

    const isAdmin = memberInfo?.admin === 1;

    const [sorterVisible, setSorterVisible] = useState(false)

    const getUserList = useCallback((type) => {
        const yearNum = new Date().getFullYear()
        return memberApi.getList(yearNum, type)
            .then(res => {
                let data = res.data.data;
                // 兼容接口的分组和扁平数组两种返回结构。
                if (Array.isArray(data) && data.length > 0 && !data[0].items) {
                    data = [{ title: '按编号排序', items: data }];
                }
                setUserList(data)
            })
            .catch(err => console.error(err))
    }, [])

    useEffect(() => {
        if (!memberInfo) {
            navigate("/login");
            return;
        }

        if (memberInfo.temp === '1') {
            Toast.show({ icon: 'fail', content: '请联系管理员创建账号登陆后查看！' });
            navigate("/login");
            return;
        }

        getUserList(1)
    }, [getUserList, memberInfo, navigate]);

    const goToDetails = (item) => {
        navigate(`/members/${item.id}`);
    }

    const handleEditRole = (item) => {
        const tagInfo = getLegacyRoleTag(item);
        const isPresetTag = ROLE_TAG_OPTIONS.some(option => option.value === tagInfo && option.value !== CUSTOM_TAG_VALUE);

        setRoleDraft({
            selectedTag: isPresetTag ? tagInfo : (tagInfo ? CUSTOM_TAG_VALUE : ''),
            customTag: isPresetTag ? '' : tagInfo,
            leadsTeam: Number(item.standingMember) === 1,
            tempMember: normalizeMemberStatus(item.tempMember),
        });
        setEditingMember(item);
    }

    const closeRoleEditor = () => {
        if (roleSaving) return;
        setEditingMember(null);
    }

    const saveMemberRole = async () => {
        if (!editingMember || roleSaving) return;

        const tagInfo = roleDraft.selectedTag === CUSTOM_TAG_VALUE
            ? roleDraft.customTag.trim()
            : roleDraft.selectedTag;

        if (roleDraft.selectedTag === CUSTOM_TAG_VALUE && !tagInfo) {
            Toast.show({ icon: 'fail', content: '请输入自定义职务名称' });
            return;
        }

        // 无职务且不参与带队时为普通队员；有职务但不带队时 standingMember=2。
        const standingMember = roleDraft.leadsTeam ? 1 : (tagInfo ? 2 : null);
        setRoleSaving(true);
        Toast.show({ icon: 'loading', content: '保存中...', duration: 0 });

        try {
            const response = await memberApi.updateRole({
                memberId: editingMember.id,
                tagInfo: tagInfo || null,
                standingMember,
                tempMember: roleDraft.tempMember,
            });
            if (response.data?.code !== 200) {
                throw new Error(response.data?.msg || '修改失败');
            }
            setEditingMember(null);
            await getUserList(orderInfo ? 1 : null);
            Toast.show({ icon: 'success', content: '球员信息已更新' });
        } catch (error) {
            Toast.show({
                icon: 'fail',
                content: error?.response?.data?.msg || error?.message || '修改失败',
            });
        } finally {
            setRoleSaving(false);
        }
    }

    // Render Tag Logic (Glass Badges)
    const renderRoleBadges = (item) => {
        const badges = [];
        const roleTag = getLegacyRoleTag(item);

        // tagInfo 只负责显示名号；standingMember=1 仅通过颜色表达其具备带队资格。
        if (roleTag) {
            badges.push(
                <span key='tag' className={`glass-badge ${Number(item.standingMember) === 1 ? 'manager' : 'formal'}`}>
                    {roleTag}
                </span>
            );
        }

        // Temp/Informal overlap check
        if (item.tempMember === 1) badges.push(<span key='temp' className="glass-badge temp">临时</span>);
        if (item.tempMember === 3) badges.push(<span key='informal' className="glass-badge">非正式</span>);
        if (item.tempMember === 4) badges.push(<span key='leave' className="glass-badge">离队</span>);

        return <div className="member-badges-inline">{badges}</div>;
    };

    // 球员列表恢复为原有紧凑卡片，仅保留上一版已经确认的标签对齐方式。
    const MemberCard = ({ item, isInformal }) => (
        <div
            className={`member-card ${isInformal ? 'informal-item' : ''}`}
            onClick={() => goToDetails(item)}
            onContextMenu={(event) => {
                if (isAdmin) {
                    event.preventDefault();
                    handleEditRole(item);
                }
            }}
        >
            <div className="member-card-left">
                {/* 列表统一展示队员默认头像，没有头像时使用科朋队徽兜底。 */}
                <img
                    className="member-avatar"
                    src={item.image ? `${item.image}${Icon.ImageFooter}` : Icon.KEPENG_LOGO}
                    alt={`${item.name || '队员'}头像`}
                    loading="lazy"
                />
                <div className="member-jersey-num">{item.memberNum}</div>

                <div className="member-info-col">
                    <div className="member-name-row">
                        <span className="member-name">{item.name}</span>
                        {renderRoleBadges(item)}
                    </div>
                </div>
            </div>

            <div className="member-card-right">
                {isAdmin ? (
                    <div className="member-admin-actions">
                        <button
                            type="button"
                            className="member-card-action member-card-action--edit"
                            aria-label={`编辑${item.name}的个人资料`}
                            onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/members/${item.id}/edit`);
                            }}
                        >
                            <EditSOutline />
                        </button>
                        <button
                            type="button"
                            className="member-card-action member-card-action--role"
                            aria-label={`修改${item.name}的职务与状态`}
                            onClick={(event) => {
                                event.stopPropagation();
                                handleEditRole(item);
                            }}
                        >
                            <FilterOutline />
                        </button>
                    </div>
                ) : (
                    <RightOutline color='var(--member-text-muted)' />
                )}
            </div>
        </div>
    );

    // Group Renderer
    const renderGroup = (group) => {
        if (!group.items || group.items.length === 0) return null;
        return (
            <div key={group.title}>
                <div className="member-section-header">{group.title}</div>
                {group.items.map(item => <MemberCard key={item.id} item={item} />)}
            </div>
        )
    };

    // Filter Logic
    const getFilteredList = () => {
        if (!userList || userList.length === 0) return { main: [], informal: [], temp: [] };

        const mainGroups = [];
        const informalList = [];
        const tempItems = [];
        const seenIds = new Set();

        userList.forEach(group => {
            const mainGroupItems = [];

            // Check if group.items exists
            (group.items || []).forEach(item => {
                const matchesSearch = !searchText || item.name.toLowerCase().includes(searchText.toLowerCase());
                if (!matchesSearch) return;

                if (item.tempMember === 1) {
                    // Temp Member (Tab 2)
                    if (!seenIds.has(item.id)) {
                        tempItems.push(item);
                        seenIds.add(item.id);
                    }
                } else if ([3, 4].includes(item.tempMember)) {
                    // Informal/Leave (Bottom of Tab 1) - Revert to user original logic (3,4)
                    if (!seenIds.has(item.id)) {
                        informalList.push(item);
                        seenIds.add(item.id);
                    }
                } else if (item.tempMember === null || item.tempMember === undefined) {
                    // 正式队员严格对应 tempMember=null；tempMember=2 的外战队伍不会进入球员列表。
                    mainGroupItems.push(item);
                }
                // 其他状态值不进入当前两个球员列表。
            });

            if (mainGroupItems.length > 0) {
                mainGroups.push({ ...group, items: mainGroupItems });
            }
        });

        return { main: mainGroups, informal: informalList, temp: tempItems };
    };

    const { main, informal, temp } = getFilteredList();

    // Sorter Dropdown Content (Redesigned)
    const SorterContent = (
        <div className="sorter-panel">
            <div className="sorter-panel-title">
                列表排序
            </div>
            <div className="sorter-options">
                <div
                    className={`sorter-option-item ${!orderInfo ? 'active' : ''}`}
                    onClick={() => {
                        setOrderInfo(null);
                        getUserList(null);
                        setSorterVisible(false);
                    }}
                >
                    <span className="sorter-icon">🔤</span>
                    <span className="sorter-text">按姓名首字母</span>
                    {!orderInfo && <div className="sorter-check">✓</div>}
                </div>

                <div
                    className={`sorter-option-item ${orderInfo ? 'active' : ''}`}
                    onClick={() => {
                        setOrderInfo(1);
                        getUserList(1);
                        setSorterVisible(false);
                    }}
                >
                    <span className="sorter-icon">🔢</span>
                    <span className="sorter-text">按球员编号</span>

                    {orderInfo && <div className="sorter-check">✓</div>}
                </div>
            </div>

            {/* Divider for Admin Actions if needed */}
            {isAdmin && <div className="sorter-admin-block">
                <div className="sorter-admin-title">管理员操作</div>
                <div
                    className="sorter-option-item"
                    onClick={() => {
                        navigate('/uploadMember');
                        setSorterVisible(false);
                    }}
                >
                    <span className="sorter-icon">➕</span>
                    <span className="sorter-text">添加新队员</span>
                </div>
            </div>}
        </div>
    );

    return (
        <div className="member-container">
            {/* Sticky Glass Header */}
            <div className="member-header-sticky">
                {/* Custom Tabs & Filter Row - Flex Container */}
                <div className="member-header-row">

                    {/* Tabs take up remaining space */}
                    <div className="member-custom-tabs">
                        <div
                            className={`member-tab-item ${activeTab === 'members' ? 'active' : ''}`}
                            onClick={() => setActiveTab('members')}
                        >
                            科朋队员
                        </div>
                        <div
                            className={`member-tab-item ${activeTab === 'tempMembers' ? 'active' : ''}`}
                            onClick={() => setActiveTab('tempMembers')}
                        >
                            临时队员
                        </div>
                    </div>

                    {/* Sorter Button (Right) - Styled to blend in */}
                    <div
                        className="member-filter-btn"
                        onClick={() => setSorterVisible(true)}
                    >
                        <FilterOutline fontSize={20} color="var(--member-text-muted)" />
                    </div>
                </div>
            </div>

            {/* Search Bar Container */}
            <div className="member-search-section">
                <SearchBar
                    placeholder='输入姓名搜索球员'
                    value={searchText}
                    onChange={val => setSearchText(val)}
                    onClear={() => setSearchText('')}
                    // 搜索框仅调整视觉变量，搜索逻辑仍保持原来的前端过滤。
                    className="member-search"
                />
            </div>

            {/* List Content */}
            <PullToRefresh onRefresh={async () => getUserList(orderInfo ? 1 : null)}>
                <div className="member-list-content">
                    {activeTab === 'members' ? (
                        <>
                            {main.length > 0 ? main.map(renderGroup) : (
                                informal.length === 0 && <Empty description='暂无正式队员' style={{ marginTop: '20vh' }} />
                            )}

                            {/* Informal Section */}
                            {informal.length > 0 && (
                                <>
                                    <div className="informal-section-divider">非正式 / 离队</div>
                                    {informal.map(item => <MemberCard key={item.id} item={item} isInformal={true} />)}
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            {temp.length > 0 ? (
                                <div>
                                    <div className="member-section-header" style={{ marginTop: 0 }}>临时队员</div>
                                    {temp.map(item => <MemberCard key={item.id} item={item} />)}
                                </div>
                            ) : (
                                <Empty description='暂无临时队员' style={{ marginTop: '20vh' }} />
                            )}
                        </>
                    )}
                </div>
            </PullToRefresh>

            {/* 职务与状态编辑统一使用单层底部面板，不再打开居中 Dialog。 */}
            <Popup
                visible={Boolean(editingMember)}
                position='bottom'
                bodyClassName="member-role-popup"
                onMaskClick={closeRoleEditor}
                destroyOnClose
            >
                <div className="member-role-sheet">
                    <div className="member-role-sheet-header">
                        <button type="button" className="member-role-sheet-cancel" onClick={closeRoleEditor}>取消</button>
                        <div className="member-role-sheet-heading">
                            <strong>修改职务与状态</strong>
                            <span>{editingMember?.name || ''}</span>
                        </div>
                        <button
                            type="button"
                            className="member-role-sheet-save"
                            disabled={roleSaving}
                            onClick={saveMemberRole}
                        >
                            {roleSaving ? '保存中' : '保存'}
                        </button>
                    </div>

                    <div className="member-role-sheet-content">
                        <section className="member-role-section">
                            <div className="member-role-section-head">
                                <strong>职务</strong>
                                <span>选择预设名号，或输入自定义标签</span>
                            </div>
                            <div className="member-role-tag-grid">
                                {ROLE_TAG_OPTIONS.map(option => (
                                    <button
                                        type="button"
                                        key={option.value || 'empty-role'}
                                        className={`member-role-choice ${roleDraft.selectedTag === option.value ? 'is-selected' : ''}`}
                                        onClick={() => setRoleDraft(previous => ({ ...previous, selectedTag: option.value }))}
                                    >
                                        <span className="member-role-choice-icon">{option.icon}</span>
                                        <span>{option.label}</span>
                                        {roleDraft.selectedTag === option.value && <i>✓</i>}
                                    </button>
                                ))}
                            </div>
                            {roleDraft.selectedTag === CUSTOM_TAG_VALUE && (
                                <div className="member-role-custom-input">
                                    <Input
                                        value={roleDraft.customTag}
                                        maxLength={20}
                                        clearable
                                        placeholder="输入自定义职务，例如：赛事总监"
                                        onChange={value => setRoleDraft(previous => ({ ...previous, customTag: value }))}
                                    />
                                </div>
                            )}
                        </section>

                        <section className="member-role-section">
                            <div className="member-role-section-head">
                                <strong>是否参与带队</strong>
                                <span>仅正式队员且选择参与，才进入队长候选范围</span>
                            </div>
                            <div className="member-leader-options">
                                <button
                                    type="button"
                                    className={`member-leader-choice ${roleDraft.leadsTeam ? 'is-selected' : ''}`}
                                    onClick={() => setRoleDraft(previous => ({ ...previous, leadsTeam: true }))}
                                >
                                    <span>🧢</span><strong>参与带队</strong>
                                </button>
                                <button
                                    type="button"
                                    className={`member-leader-choice ${!roleDraft.leadsTeam ? 'is-selected' : ''}`}
                                    onClick={() => setRoleDraft(previous => ({ ...previous, leadsTeam: false }))}
                                >
                                    <span>👤</span><strong>不参与带队</strong>
                                </button>
                            </div>
                        </section>

                        <section className="member-role-section">
                            <div className="member-role-section-head">
                                <strong>队员状态</strong>
                                <span>转为正式队员时，入队日期会更新为当天</span>
                            </div>
                            <div className="member-status-grid">
                                {MEMBER_STATUS_OPTIONS.map(option => (
                                    <button
                                        type="button"
                                        key={option.value === null ? 'formal' : option.value}
                                        className={`member-status-choice ${roleDraft.tempMember === option.value ? 'is-selected' : ''}`}
                                        onClick={() => setRoleDraft(previous => ({ ...previous, tempMember: option.value }))}
                                    >
                                        <span className="member-status-icon">{option.icon}</span>
                                        <span className="member-status-copy">
                                            <strong>{option.label}</strong>
                                            <small>{option.description}</small>
                                        </span>
                                        {roleDraft.tempMember === option.value && <i>✓</i>}
                                    </button>
                                ))}
                            </div>
                        </section>
                    </div>
                </div>
            </Popup>

            {/* Popup Logic for Sorter */}
            <Popup
                visible={sorterVisible}
                onMaskClick={() => setSorterVisible(false)}
                bodyStyle={{ borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}
            >
                {SorterContent}
            </Popup>
        </div>
    );
}
